﻿/*
 * Purpose: Compose T&Cs acceptance with validation and parent sync via context.
 * Boundary: Component; owns UI composition and hook state; no data fetching.
 * Invariants:
 * - Renders T&Cs text with acceptance button below.
 * - Validation passes only when button is checked (accepted).
 * - Exposes isValid and checked state via context to parent.
 * - ASCII only; comments and strings use plain ASCII.
 */

// Imports
import { createContext, useContext, useEffect } from 'react';
import { useListOfCheckboxes } from './listofcheckboxes/listofcheckboxes-hooks';
import ListOfCheckboxesLayout from './listofcheckboxes/listofcheckboxes-layout';

// Context for sharing acceptance state upward
export const CheckboxContext = createContext();

// Component ListOfCheckboxes:
// Headless wrapper composing layout and hook state, syncs with parent via context
export default function ListOfCheckboxes({ options = [], showValidationLabel = true, label }) {
    // State Management - initialize hook with options
    const props = useListOfCheckboxes({ options });

    // Context - get setters from parent
    const { setCheckboxState } = useContext(CheckboxContext) || {};

    // Effect: Update parent via context
    useEffect(() => {
        if (setCheckboxState) {
            setCheckboxState({ isValid: props.isValid, checked: props.checked });
        }
    }, [props.isValid, props.checked, setCheckboxState]);

    // Rendering - delegate structure to layout component
    return (
        <ListOfCheckboxesLayout
            showValidationLabel={showValidationLabel}
            label={label}
            {...props}
        />
    );
}

// Component metadata - support React DevTools naming
ListOfCheckboxes.displayName = 'ListOfCheckboxes';